import React, { useState, useMemo, useCallback } from 'react';
import { Mui } from '@osu/react-ui';
import { validatePreferences, canSubmitPreferences, REQUIRED_RANKS, MAX_RANK } from '../../../util/scholars/preferenceValidation';
import { useSubmitScholarProgramPreferencesMutation } from '../../../services/graphql/api';

// U9b — Scholars ranked preferencing form (C2) + alternate opt-in (C3). Ranks 1–3 required /
// 4–8 optional / no duplicates (validation duplicated per-repo). Rankings + alternate opt-in are
// submitted together in one owner-scoped mutation (matches the backend submitScholarsPreferences).
const RANKS = Array.from({ length: MAX_RANK }, (_, i) => i + 1);

export default function ScholarsPreferencingForm({ applicantEmployeeId, term = undefined, programs = [] }) {
    const [selections, setSelections] = useState({}); // { [rank]: subProgramId }
    const [alternateOptIn, setAlternateOptIn] = useState(true);
    const [submit, { isLoading }] = useSubmitScholarProgramPreferencesMutation();
    const [message, setMessage] = useState('');

    // Validation view-model keyed by programId (used by the shared validator).
    const ranksForValidation = useMemo(
        () => Object.entries(selections)
            .filter(([, subProgramId]) => !!subProgramId)
            .map(([rank, subProgramId]) => ({ rank: Number(rank), programId: subProgramId })),
        [selections]
    );

    const { errors } = useMemo(() => validatePreferences(ranksForValidation), [ranksForValidation]);
    const canSubmit = canSubmitPreferences(ranksForValidation) && !isLoading;

    const onSelect = useCallback((rank) => (e) => {
        const subProgramId = e.target.value;
        setSelections((prev) => ({ ...prev, [rank]: subProgramId }));
    }, []);

    const onSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!canSubmitPreferences(ranksForValidation)) { return; }
        setMessage('');
        // Backend shape: rankings = [{ subProgramId, rank }]. term is optional — the backend
        // resolves it server-side from the applicant's Scholars records; only send it if provided.
        const rankings = ranksForValidation.map(({ rank, programId }) => ({ subProgramId: programId, rank }));
        const payload = { applicantEmployeeId, rankings, alternateOptIn };
        if (term) { payload.term = term; }
        try {
            await submit(payload).unwrap();
            setMessage('Your preferences were submitted.');
        } catch (err) {
            setMessage('Unable to submit preferences. Please try again.');
        }
    }, [submit, applicantEmployeeId, term, ranksForValidation, alternateOptIn]);

    return (
        <Mui.Paper component="form" onSubmit={onSubmit} sx={{ p: { xs: 1.5, sm: 2 }, width: '100%' }} data-testid="scholars-preferencing-form">
            <Mui.Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>Rank Your Scholars Programs</Mui.Typography>
            <Mui.Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                You will be reviewed for each Scholars program in ranked order and if selected, receive an offer for one program. Ranks 1–3 are required. Ranks 4–6 are optional. Each program may be chosen once.
            </Mui.Typography>
            <Mui.Stack spacing={1}>
                {RANKS.map((rank) => (
                    <Mui.TextField
                        key={rank}
                        select
                        label={`Rank ${rank}${REQUIRED_RANKS.includes(rank) ? ' (required)' : ''}`}
                        value={selections[rank] ?? ''}
                        onChange={onSelect(rank)}
                        inputProps={{ 'data-testid': `preference-rank-${rank}` }}
                    >
                        <Mui.MenuItem value=""><em>None</em></Mui.MenuItem>
                        {programs.map((p) => {
                            const programValue = p.key ?? p.id ?? p.subProgramId;
                            const programName = p.name ?? p.label;
                            // Closed programs still appear in the list but are not selectable — shown
                            // grayed out and struck through with a "(Closed)" suffix. The backend
                            // (has607) sends isOpen=false for closed programs; also honor explicit
                            // closed/isClosed/status flags if present. A missing isOpen is NOT treated
                            // as closed (back-compat with data that predates the flag).
                            const isClosed =
                                p.isOpen === false ||
                                p.closed === true ||
                                p.isClosed === true ||
                                p.status === 'CLOSED';
                            return (
                                <Mui.MenuItem
                                    key={programValue}
                                    value={programValue}
                                    disabled={isClosed}
                                    sx={isClosed ? { color: 'text.disabled' } : undefined}
                                >
                                    {isClosed ? `${programName} (Closed)` : programName}
                                </Mui.MenuItem>
                            );
                        })}
                    </Mui.TextField>
                ))}
            </Mui.Stack>
            <Mui.Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start' }}>
                <Mui.Checkbox
                    checked={alternateOptIn}
                    onChange={(e) => setAlternateOptIn(e.target.checked)}
                    inputProps={{ 'data-testid': 'alternate-opt-in', 'aria-label': 'Consider me for other programs if I am not admitted to a ranked choice' }}
                    sx={{ pt: 0, pl: 0 }}
                />
                <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
                    <Mui.Typography variant="body2">
                        Consider me for other programs if I'm not admitted to a ranked choice
                    </Mui.Typography>
                    <Mui.Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        You're opted in by default. Uncheck this if you'd rather not be considered for other programs.
                    </Mui.Typography>
                </Mui.Box>
            </Mui.Box>
            {errors.length > 0 && (
                <Mui.Box role="alert" sx={{ mt: 1 }}>
                    {errors.map((err) => (
                        <Mui.Typography key={err} variant="caption" color="error" display="block">{err}</Mui.Typography>
                    ))}
                </Mui.Box>
            )}
            <Mui.Button type="submit" variant="contained" disabled={!canSubmit} sx={{ mt: 2 }} data-testid="preferencing-submit">
                {isLoading ? 'Submitting…' : 'Submit Preferences'}
            </Mui.Button>
            <Mui.Box aria-live="polite" role="status" sx={{ mt: 1 }}>{message}</Mui.Box>
        </Mui.Paper>
    );
}
